import { PortalModule } from "@angular/cdk/portal";
import { CdkStepperModule } from "@angular/cdk/stepper";
import { CommonModule } from "@angular/common";
import { ComponentFactoryResolver, NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatLegacyButtonModule as MatButtonModule } from "@angular/material/legacy-button";
import { MatLegacyCardModule as MatCardModule } from "@angular/material/legacy-card";
import { MatLegacyCheckboxModule as MatCheckboxModule } from "@angular/material/legacy-checkbox";
import {
	MatLegacyNativeDateModule as MatNativeDateModule,
	MatLegacyOptionModule as MatOptionModule,
} from "@angular/material/legacy-core";
import { MatLegacyDialogModule as MatDialogModule } from "@angular/material/legacy-dialog";
import { MatLegacyFormFieldModule as MatFormFieldModule } from "@angular/material/legacy-form-field";
import { MatLegacyInputModule as MatInputModule } from "@angular/material/legacy-input";
import { MatLegacyListModule as MatListModule } from "@angular/material/legacy-list";
import { MatLegacyMenuModule as MatMenuModule } from "@angular/material/legacy-menu";
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from "@angular/material/legacy-progress-spinner";
import { MatLegacyRadioModule as MatRadioModule } from "@angular/material/legacy-radio";
import { MatLegacySelectModule as MatSelectModule } from "@angular/material/legacy-select";
import { MatLegacySlideToggleModule as MatSlideToggleModule } from "@angular/material/legacy-slide-toggle";
import { MatLegacySnackBarModule as MatSnackBarModule } from "@angular/material/legacy-snack-bar";
import { MatLegacyTabsModule as MatTabsModule } from "@angular/material/legacy-tabs";
import { MatLegacyTooltipModule as MatTooltipModule } from "@angular/material/legacy-tooltip";
import { MatStepperModule } from "@angular/material/stepper";
import { MatToolbarModule } from "@angular/material/toolbar";
import { RouterModule } from "@angular/router";
import { RealtimeNotificationsModule } from "@obg/common.realtime-notifications";
import { OpenIdConnectModule, TwoFactorAuthVerificationModule } from "@obg/common.verification";
import { MatCommonModule } from "@obg/mat.ui.common";
import { TermsAndConditionsModule } from "@obg/mat.ui.common/terms-and-conditions";
import { DialogLayoutModule, MatCoreModule, OverlaySpinnerModule } from "@obg/mat.ui.core";
import { CommonModule as ObgCommonModule } from "@obg/ngx.common";
import { JurisdictionPrefixModule } from "@obg/ngx.core";
import { CoreContentModule } from "@obg/ngx.core.content";
import { GlobalModule } from "@obg/ngx.global";
import { RouterModule as NgxRouterModule } from "@obg/ngx.router";
import {
	AnimatedIconsModule,
	CommonUiModule,
	CommonValidationRendererModule,
	FlowModule,
	LoginLimitReminderContainer,
	RssFeedModule,
	TogglePasswordModule,
} from "@obg/ngx.ui.common";
import { CompactFooterModule, ComponentFactoryService, CoreUiModule } from "@obg/ngx.ui.core";
import { GlobalUiModule } from "@obg/ngx.ui.global";
import { GlobalUiLazyImageModule } from "@obg/ngx.ui.global.lazy-image";

import { LoginFortyDaysReviewDialogComponent } from "../login/login-forty-days-review-dialog/login-forty-days-review-dialog.component";
import { MarketingConsentRenewalComponent } from "../marketing-consent-renewal/marketing-consent-renewal.component";
import { RenewPasswordModule } from "../renew-password/renew-password.module";
import { CommonLoginSdkModule } from "../sdk/module";
import { SgaSetPasswordModule } from "../sga-set-password/sga-set-password.module";
import { VerificationDialogModule } from "../verification-dialog/verification-dialog.module";

import { LoginConsentsContainer } from "./consents/login-consents.container";
import { LoginContainer } from "./container/login.container";
import { DgaLoginComponent } from "./dga/dga-login.component";
import { IdSelectComponent } from "./dga/id-select/id-select.component";
import { MitIdLoginButtonComponent } from "./dga/mitid-login-button/mitid-login-button.component";
import { OidcRegistrationContainer } from "./dga/oidc-registration-container/oidc-registration.container";
import { RequestCpfDialogComponent } from "./face-auth/request-cpf-dialog/request-cpf-dialog.component";
import { GenericLoginContainer } from "./generic/generic-login.container";
import { LoginYearlyReviewContainer } from "./login-yearly-review/login-yearly-review.container";
import { LoginFlowContainer } from "./new-login-flow/containers/login-flow/login-flow.container";
import { PokerLoginVerificationComponent } from "./poker-login/login-verification/poker-login-verification.component";
import { PokerLoginContainer } from "./poker-login/poker-login.container";
import { SgaLoginContainer } from "./sga/login/sga-login.container";
import { LoginBankIdContainer } from "./sga/login-bankid/login-bankid.container";
import { SgaLoginFormContainer } from "./sga/login-form/sga-login-form.container";
import { QuickSgaLoginContainer } from "./sga/quick-login/quick-sga-login.container";
import { SgaLoginNoticeComponent } from "./sga/sga-login-notice/sga-login-notice.component";
import { SgaLoginNoticeModalComponent } from "./sga/sga-login-notice-modal/sga-login-notice-modal.component";
import { VerificationSgaLoginContainer } from "./sga/verification-login/verification-sga-login.container";
import { VerificationSgaLoginWithDialogContainer } from "./sga/wrappers/verification-login-with-dialog/verification-login-with-dialog.container";
import { SmartIdLoginFormContainer } from "./smart-id/login-form/smart-id-login-form.container";
import { SocialLoginContainer } from "./social-login/social-login.container";
import { SuspiciousLoginRequestCodeComponent } from "./suspicious-login/request-code/suspicious-login-request-code.component";
import { SuspiciousLoginContainer } from "./suspicious-login/suspicious-login.container";
import { SuspiciousLoginValidateCodeComponent } from "./suspicious-login/validate-code/suspicious-login-validate-code.component";
import { LoginWithDialogContainer } from "./wrappers/login-with-dialog/login-with-dialog.container";
import { LoginWithServiceContainer } from "./wrappers/login-with-service/login-with-service.container";

export const MAT_COMMON_LOGIN_UI_DIRECTIVES: any[] = [
	PokerLoginContainer,
	PokerLoginVerificationComponent,
	LoginContainer,
	LoginWithServiceContainer,
	LoginWithDialogContainer,
	GenericLoginContainer,
	SgaLoginFormContainer,
	SgaLoginContainer,
	LoginBankIdContainer,
	SmartIdLoginFormContainer,
	LoginFlowContainer,
	VerificationSgaLoginContainer,
	VerificationSgaLoginWithDialogContainer,
	QuickSgaLoginContainer,
	DgaLoginComponent,
	MitIdLoginButtonComponent,
	LoginConsentsContainer,
	SuspiciousLoginValidateCodeComponent,
	SuspiciousLoginContainer,
	SuspiciousLoginRequestCodeComponent,
	SgaLoginNoticeModalComponent,
	SgaLoginNoticeComponent,
	LoginLimitReminderContainer,
	IdSelectComponent,
	OidcRegistrationContainer,
	MarketingConsentRenewalComponent,
	LoginFortyDaysReviewDialogComponent,
	LoginYearlyReviewContainer,
	RequestCpfDialogComponent,
];

const MAT_ENTRY_COMPONENTS = [
	LoginContainer,
	LoginWithServiceContainer,
	LoginWithDialogContainer,
	VerificationSgaLoginContainer,
	VerificationSgaLoginWithDialogContainer,
	LoginConsentsContainer,
	SuspiciousLoginValidateCodeComponent,
	SuspiciousLoginContainer,
	SuspiciousLoginRequestCodeComponent,
	LoginLimitReminderContainer,
];

export const MAT_MODULES: any[] = [
	CdkStepperModule,
	MatButtonModule,
	MatCheckboxModule,
	MatDialogModule,
	MatDividerModule,
	MatFormFieldModule,
	MatIconModule,
	MatMenuModule,
	MatNativeDateModule,
	MatToolbarModule,
	MatInputModule,
	MatSlideToggleModule,
	MatCardModule,
	MatListModule,
	MatSnackBarModule,
	MatStepperModule,
	MatProgressSpinnerModule,
	MatBadgeModule,
	MatOptionModule,
	MatSelectModule,
	MatButtonToggleModule,
	MatExpansionModule,
	MatTabsModule,
	MatTooltipModule,
	MatRadioModule,
	PortalModule,
];

@NgModule({
	imports: [
		CommonModule,
		ObgCommonModule,
		CoreContentModule,
		CoreUiModule,
		MatCommonModule,
		MAT_MODULES,
		FormsModule,
		ReactiveFormsModule,
		GlobalModule,
		MatCoreModule,
		MatButtonModule,
		RouterModule,
		NgxRouterModule,
		GlobalUiModule,
		RssFeedModule,
		GlobalUiLazyImageModule,
		CompactFooterModule,
		OverlaySpinnerModule,
		DialogLayoutModule,
		MatDatepickerModule,
		TogglePasswordModule,
		CommonValidationRendererModule,
		JurisdictionPrefixModule,
		CommonUiModule,
		CommonLoginSdkModule,
		RealtimeNotificationsModule,
		SgaSetPasswordModule,
		VerificationDialogModule,
		AnimatedIconsModule,
		FlowModule,
		OpenIdConnectModule,
		TwoFactorAuthVerificationModule,
		SocialLoginContainer,
		RenewPasswordModule,
		TermsAndConditionsModule,
	],
	exports: [MAT_COMMON_LOGIN_UI_DIRECTIVES],
	declarations: [MAT_COMMON_LOGIN_UI_DIRECTIVES],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MatCommonLoginModule {
	constructor(componentFactoryService: ComponentFactoryService, resolver: ComponentFactoryResolver) {
		componentFactoryService.registerLazyLoadedResolver(resolver, ...MAT_ENTRY_COMPONENTS);
	}
}
